import { WorkerPersonnelRequestActions, type StaffingOperationsPort } from '../../staffing/public';
import { workflowPersonnelRequestTarget } from '../models/workflowPersonnelRequestTarget';
import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { DownloadOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type {
  CollectionResponse,
  WorkflowRequest,
  WorkflowRequestFilters,
} from '../model';
import type {
  WorkflowPort,
  WorkflowRoutePort,
} from '../port';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { formatWorkflowDate } from '../models/workflowRequestPageModel';
import { registerNavigationGuard } from '../../../shared/navigationGuard';
import {
  ResourcePage,
  ResourceCollection,
  type ResourceCollectionState,
  type WorkspaceSurfacePresentation,
} from '../../../design-system';
import type { WorkflowApprovalSettingsPort } from '../approval-settings/public';
import type { EmployeeDirectoryPort } from '../../people/public';
import {
  fixtureRequests,
  fixtureTemplates,
} from '../fixtures/workflowPageFixtures';
import { RequestStatus, statusLabel } from '../components/WorkflowStatus';
import type { WorkflowAccessProps } from '../workflowPageSupport';
import {
  workflowRequestIDFromLocation,
  workflowRequestLoadingPlaceholder,
  workflowRequestTitle,
} from '../models/workflowRequestPageModel';
import { workflowAdminFiltersFromSearch, workflowAdminRouteSearch, workflowRequesterLabel, workflowRequestDisplayStatus } from '../models/workflowRequestListModel';
import { WorkflowRequestDocumentWorkspace } from '../components/WorkflowRequestDocumentWorkspace';

export type WorkflowRequestsPageProps = {
  port: WorkflowPort;
  staffingPort?: StaffingOperationsPort;
  route: WorkflowRoutePort;
  approvalSettingsPort: WorkflowApprovalSettingsPort;
  directoryPort: EmployeeDirectoryPort;
  fixtureMode: boolean;
  canBrowseTemplates?: boolean;
} & WorkflowAccessProps;

export function WorkflowRequestsPage({ port, staffingPort, route, approvalSettingsPort, directoryPort, fixtureMode, readOnly, scopes, canBrowseTemplates = true }: WorkflowRequestsPageProps) {
  const canExport = !readOnly && scopes.includes('workflow.request.export');
  const canManagePersonnel = !readOnly && (fixtureMode || scopes.includes('staffing.write'));
  const canCancelPersonnel = canManagePersonnel && (fixtureMode || scopes.includes('business_process.cancel'));
  const [personnelBusy, setPersonnelBusy] = useState(false);
  const templates = useAsyncResource(() => !canBrowseTemplates ? Promise.resolve([]) : fixtureMode ? Promise.resolve(fixtureTemplates) : port.listWorkflowTemplates(true).then((response) => response.data), [port, fixtureMode, canBrowseTemplates]);
  const [filters, setFilters] = useState<WorkflowRequestFilters>(() => workflowAdminFiltersFromSearch(window.location.search));
  const resource = useAsyncResource<CollectionResponse<WorkflowRequest>>(() => fixtureMode
    ? Promise.resolve({ total: fixtureRequests.length, data: fixtureRequests })
    : port.listWorkflowRequests(filters), [port, fixtureMode, filters]);
  const totalResource = useAsyncResource<CollectionResponse<WorkflowRequest>>(() => fixtureMode
    ? Promise.resolve({ total: fixtureRequests.length, data: fixtureRequests })
    : port.listWorkflowRequests({ all: true }), [port, fixtureMode]);
  const rows = resource.data?.data ?? [];
  const [selected, setSelected] = useState<WorkflowRequest>();
  const [error, setError] = useState('');
  const [detailRetry, setDetailRetry] = useState(0);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedError, setSelectedError] = useState('');
  const [routeRequestID, setRouteRequestID] = useState<string | undefined>(() => workflowRequestIDFromLocation());
  const selectedSequence = useRef(0);
  const [presentation, setPresentation] = useState<WorkspaceSurfacePresentation>(() => new URLSearchParams(window.location.search).get('presentation') === 'fullscreen' ? 'standalone' : 'dialog');
  const [commentDirty, setCommentDirty] = useState(false);
  const [commentBusy, setCommentBusy] = useState(false);
  const skipGuard = useRef(false);
  useEffect(() => {
    const unregister = registerNavigationGuard(() => skipGuard.current || (!commentBusy && !personnelBusy && (!commentDirty || window.confirm('등록하지 않은 댓글을 버리고 이동할까요?'))));
    const unload = (event: BeforeUnloadEvent) => { if (commentDirty || commentBusy || personnelBusy) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', unload);
    return () => { unregister(); window.removeEventListener('beforeunload', unload); };
  }, [commentDirty, commentBusy, personnelBusy]);
  const columns: GridColDef<WorkflowRequest>[] = [
    { field: 'title', headerName: '요청', minWidth: 240, flex: 1, valueGetter: (_value, row) => workflowRequestTitle(row) },
    { field: 'templateName', headerName: '양식', minWidth: 170, flex: 0.8 },
    { field: 'requesterName', headerName: '요청자', minWidth: 170, flex: 0.7, valueGetter: (_value, row) => workflowRequesterLabel(row.requesterName) },
    { field: 'status', headerName: '상태', width: 144, valueGetter: (_value, row) => workflowRequestDisplayStatus(row), renderCell: ({ row }) => <RequestStatus request={row} /> },
    { field: 'createdAt', headerName: '요청일', width: 120, valueFormatter: (value) => formatWorkflowDate(String(value)) },
  ];
  const exportExcel = async () => {
    if (!canExport) return;
    setError('');
    try {
      if (fixtureMode) return;
      const blob = await port.exportWorkflowRequests(filters);
      const url = URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = `workflow-requests-${new Date().toISOString().slice(0, 10)}.xlsx`; anchor.click(); URL.revokeObjectURL(url);
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Excel 파일을 내려받지 못했습니다.'); }
  };
  useEffect(() => {
    const syncRouteRequest = () => {
      setPresentation(new URLSearchParams(window.location.search).get('presentation') === 'fullscreen' ? 'standalone' : 'dialog');
      if (route.currentPage() === 'workflow-requests') {
        const next = workflowAdminFiltersFromSearch(window.location.search);
        setFilters((current) => JSON.stringify(current) === JSON.stringify(next) ? current : next);
      }
      setRouteRequestID(route.currentPage() === 'workflow-requests'
        ? workflowRequestIDFromLocation()
        : undefined);
    };
    syncRouteRequest();
    return route.subscribe(syncRouteRequest);
  }, [route]);
  useEffect(() => {
    if (!routeRequestID) {
      selectedSequence.current += 1;
      setSelected(undefined);
      setSelectedLoading(false);
      setSelectedError('');
      return undefined;
    }
    const sequence = ++selectedSequence.current;
    setSelected((current) => current?.id === routeRequestID ? current : workflowRequestLoadingPlaceholder(routeRequestID));
    setSelectedError('');
    setSelectedLoading(!fixtureMode);
    const detailRequest = fixtureMode
      ? Promise.resolve(fixtureRequests.find((request) => request.id === routeRequestID))
      : port.getWorkflowRequest(routeRequestID);
    void detailRequest.then(
      (detail) => {
        if (selectedSequence.current !== sequence) return;
        if (!detail || detail.id !== routeRequestID) {
          setSelectedError('요청 상세를 찾을 수 없습니다.');
          setSelectedLoading(false);
          return;
        }
        setSelected(detail);
        setSelectedLoading(false);
      },
      (reason: unknown) => {
        if (selectedSequence.current !== sequence) return;
        setSelectedError(reason instanceof Error ? reason.message : '요청 상세를 불러오지 못했습니다.');
        setSelectedLoading(false);
      },
    );
    return () => { if (selectedSequence.current === sequence) selectedSequence.current += 1; };
  }, [port, fixtureMode, routeRequestID, detailRetry]);
  const openRequest = (request: WorkflowRequest) => {
    route.navigate('workflow-requests', workflowAdminRouteSearch(filters, request.id));
  };
  const closeRequest = () => {
    route.navigate('workflow-requests', workflowAdminRouteSearch(filters));
  };
  const changeFilters = (patch: Partial<WorkflowRequestFilters>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    route.navigate('workflow-requests', workflowAdminRouteSearch(next), { replace: true });
  };
  const collectionState: ResourceCollectionState = resource.loading
    ? { kind: 'loading' }
    : resource.error
      ? { kind: 'error', message: resource.error.message, onRetry: resource.reload }
      : rows.length === 0
        ? { kind: 'empty', title: '조건에 맞는 요청이 없습니다.' }
        : { kind: 'ready' };
  const personnelTarget = workflowPersonnelRequestTarget(selected);
  return <>
    <Box sx={{ display: selected && presentation === 'standalone' ? 'none' : 'contents' }} aria-hidden={Boolean(selected && presentation === 'standalone')} inert={selected && presentation === 'standalone' || undefined}>
    <ResourcePage
      title="워크플로우 요청"
      actions={canExport ? <Button variant="outlined" startIcon={<DownloadOutlined />} onClick={() => void exportExcel()}>Excel 내보내기</Button> : undefined}
    >
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {error ? <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }}>{error}</Alert> : null}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResourceCollection
            state={collectionState}
            rows={rows}
            totalCount={totalResource.data?.total ?? rows.length}
            columns={columns}
            query={filters.search ?? ''}
            onQueryChange={(search) => changeFilters({ search })}
            searchPlaceholder="제목, 양식명, 요청자 또는 검색 허용 항목"
            filters={[
              { label: '양식', value: filters.templateId ?? '', onChange: (templateId) => changeFilters({ templateId }), options: [{ label: '전체', value: '' }, ...(canBrowseTemplates ? (templates.data ?? []).map((template) => ({ label: template.displayName, value: template.id })) : Array.from(new Map((totalResource.data?.data ?? []).map((request) => [request.templateId, { label: request.templateName, value: request.templateId }])).values()))] },
              { label: '상태', value: filters.status ?? '', onChange: (status) => changeFilters({ status }), options: [{ label: '전체', value: '' }, ...['DRAFT', 'NEEDS_CHANGES', 'IN_PROGRESS', 'AWAITING_EFFECTIVE', 'EXECUTION_UNVERIFIED', 'COMPLETED', 'DENIED', 'CANCELLED'].map((status) => ({ label: statusLabel[status], value: status }))] },
              { label: '요청 시작일', kind: 'date', value: filters.dateFrom ?? '', onChange: (dateFrom) => changeFilters({ dateFrom }) },
              { label: '요청 종료일', kind: 'date', value: filters.dateTo ?? '', onChange: (dateTo) => changeFilters({ dateTo }) },
            ]}
            onRowClick={({ row }) => openRequest(row)}
          />
        </Box>
      </Box>
    </ResourcePage>
    </Box>
    {selected ? <WorkflowRequestDocumentWorkspace
      port={port}
      approvalSettingsPort={approvalSettingsPort}
      directoryPort={directoryPort}
      request={selected}
      canManageContracts={!readOnly && (fixtureMode || scopes.includes('rewards.write'))}
      fixtureMode={fixtureMode}
      loading={selectedLoading}
      error={selectedError || undefined}
      onRetry={selectedError ? () => setDetailRetry((value) => value + 1) : undefined}
      onRefreshRequest={async () => {
        const id = selected.id; const sequence = selectedSequence.current;
        const latest = await port.getWorkflowRequest(id);
        if (selectedSequence.current !== sequence) return;
        if (latest.id !== id) throw new Error('요청 상태를 확인하지 못했습니다. 다시 확인해 주세요.');
        setSelected(latest); resource.reload(); totalResource.reload();
      }}
      canComment={!selectedLoading && !selectedError && !readOnly && scopes.includes('workflow.request.comment')}
      onClose={closeRequest}
      busy={commentBusy || personnelBusy}
      footer={staffingPort && personnelTarget && canManagePersonnel && !selectedLoading && !selectedError ? <WorkerPersonnelRequestActions key={personnelTarget.id} target={personnelTarget} port={staffingPort} fixtureMode={fixtureMode} canEdit={canManagePersonnel} canCancel={canCancelPersonnel} disabled={commentBusy} onBusyChange={setPersonnelBusy} onChanged={() => { resource.reload(); totalResource.reload(); setDetailRetry(value => value + 1); }} onOpenWorkflow={reference => route.navigate('workflow-requests', workflowAdminRouteSearch(filters, reference.id))} /> : undefined}
      presentation={presentation}
      onPresentationChange={value => {
        if (commentBusy || personnelBusy || (commentDirty && !window.confirm('등록하지 않은 댓글을 버리고 화면을 전환할까요?'))) return;
        const query = workflowAdminRouteSearch(filters, selected.id);
        if (value === 'standalone') query.set('presentation', 'fullscreen');
        skipGuard.current = true;
        try { route.navigate('workflow-requests', query, { replace: true }); } finally { skipGuard.current = false; }
      }}
      onCommentDraftChange={setCommentDirty} onCommentBusyChange={setCommentBusy}
      onOpenReference={id => {
        const query = workflowAdminRouteSearch(filters, id);
        if (presentation === 'standalone') query.set('presentation', 'fullscreen');
        route.navigate('workflow-requests', query);
      }}
    /> : null}
  </>;
}
